/**
 * Domain logic over content documents. Pure functions only:
 * no I/O, no storage SDKs, no rendering concerns.
 */
import type {
  Asset,
  ExhibitionDocument,
  Installation,
  WorkDocument,
  WorkMedia,
} from "@yushi-portfolio/content-schema";

export type {
  Asset,
  Credit,
  ExhibitionDocument,
  Installation,
  LocalizedText,
  WorkDocument,
  WorkMedia,
  WorkMediaKind,
  WorkType,
} from "@yushi-portfolio/content-schema";

/** Everything the public site is built from. Published documents only. */
export type ContentSet = {
  works: WorkDocument[];
  exhibitions: ExhibitionDocument[];
  assets: Asset[];
};

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

const byDisplayOrder = <T extends { displayOrder: number }>(a: T, b: T) => a.displayOrder - b.displayOrder;

export function orderMedia(media: readonly WorkMedia[]): WorkMedia[] {
  return [...media].sort(byDisplayOrder);
}

export function orderInstallations(installations: readonly Installation[]): Installation[] {
  return [...installations].sort(byDisplayOrder);
}

/** Newest first; ongoing works sort as if they end now. Ties fall back to slug for stable output. */
export function orderWorksChronologically(works: readonly WorkDocument[]): WorkDocument[] {
  const latestYear = (w: WorkDocument) => (w.date.ongoing ? Number.POSITIVE_INFINITY : (w.date.end ?? w.date.start));
  return [...works].sort(
    (a, b) => latestYear(b) - latestYear(a) || b.date.start - a.date.start || a.slug.localeCompare(b.slug),
  );
}

/** Newest first. Exhibitions without a date go last. */
export function orderExhibitionsChronologically(exhibitions: readonly ExhibitionDocument[]): ExhibitionDocument[] {
  return [...exhibitions].sort((a, b) => {
    const da = a.startDate ?? "";
    const db = b.startDate ?? "";
    return db.localeCompare(da) || a.id.localeCompare(b.id);
  });
}

/**
 * The media item that represents the work: the first featured item,
 * otherwise the first item in display order.
 */
export function primaryMedia(work: WorkDocument): WorkMedia | undefined {
  const ordered = orderMedia(work.media);
  return ordered.find((m) => m.featured) ?? ordered[0];
}

// ---------------------------------------------------------------------------
// Reverse references (computed at build time; no index needed at this scale)
// ---------------------------------------------------------------------------

export function worksByExhibition(works: readonly WorkDocument[]): Map<string, WorkDocument[]> {
  const result = new Map<string, WorkDocument[]>();
  for (const work of works) {
    const exhibitionIds = new Set(work.installations.flatMap((i) => (i.exhibitionId ? [i.exhibitionId] : [])));
    for (const id of exhibitionIds) {
      const list = result.get(id) ?? [];
      list.push(work);
      result.set(id, list);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Content validation (beyond per-document schema validation)
// ---------------------------------------------------------------------------

export function allMediaOf(work: WorkDocument): WorkMedia[] {
  return [...work.media, ...work.installations.flatMap((i) => i.media)];
}

/**
 * Rules a work must satisfy before it may be published.
 * Schema validity is assumed; this checks editorial completeness.
 */
export function validateWorkForPublish(work: WorkDocument): string[] {
  const errors: string[] = [];
  const prefix = `work ${work.id}`;
  if (!work.title.ja.trim()) errors.push(`${prefix}: title.ja is required`);
  if (work.media.filter((m) => m.featured).length > 1) errors.push(`${prefix}: at most one media item may be featured`);

  const installationIds = new Set<string>();
  for (const installation of work.installations) {
    if (installationIds.has(installation.id)) errors.push(`${prefix}: duplicate installation id ${installation.id}`);
    installationIds.add(installation.id);
  }
  return errors;
}

/**
 * Cross-document integrity of a whole content set: unique ids and slugs,
 * and every reference resolves. Returns a list of human-readable errors.
 */
export function validateContentSet(content: ContentSet): string[] {
  const errors: string[] = [];

  const findDuplicates = (values: string[]) => values.filter((v, i) => values.indexOf(v) !== i);
  for (const id of findDuplicates(content.works.map((w) => w.id))) errors.push(`duplicate work id: ${id}`);
  for (const slug of findDuplicates(content.works.map((w) => w.slug))) errors.push(`duplicate work slug: ${slug}`);
  for (const id of findDuplicates(content.exhibitions.map((e) => e.id))) errors.push(`duplicate exhibition id: ${id}`);
  for (const slug of findDuplicates(content.exhibitions.flatMap((e) => (e.slug ? [e.slug] : []))))
    errors.push(`duplicate exhibition slug: ${slug}`);
  for (const id of findDuplicates(content.assets.map((a) => a.id))) errors.push(`duplicate asset id: ${id}`);

  const assetsById = new Map(content.assets.map((a) => [a.id, a]));
  const exhibitionIds = new Set(content.exhibitions.map((e) => e.id));

  for (const work of content.works) {
    errors.push(...validateWorkForPublish(work));
    for (const media of allMediaOf(work)) {
      const asset = assetsById.get(media.assetId);
      if (!asset) errors.push(`work ${work.id}: missing asset ${media.assetId}`);
      // Originals are never served, so a referenced image or video needs web variants.
      else if (asset.type !== "document" && !asset.variants?.length)
        errors.push(`work ${work.id}: asset ${asset.id} has no web variants`);
    }
    for (const installation of work.installations) {
      if (installation.exhibitionId && !exhibitionIds.has(installation.exhibitionId)) {
        errors.push(`work ${work.id}: installation ${installation.id} references missing exhibition ${installation.exhibitionId}`);
      }
    }
  }
  return errors;
}
